import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
} from "@fluentui/react-components";
import { useQueryClient } from "@tanstack/react-query";

// COmponents
import { GenericComboBox } from "../../../shared/components/GenericComboBox";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import { CiTypeTemplate } from "../../citypes/components/CiItemTemplates";

// Business logic
import { readCiStatuses } from "../../cistatuses/services/ciStatusApi";
import { readCiTypes } from "../../citypes/services/ciTypesApi";
import { readSites } from "../../sites/services/siteApi";
import { readUsers } from "../../users/services/userApi";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";
import { parseQueryParams, buildQueryString } from "../../../shared/services/queryParamsService";

const CiFiltersPanel = ({ isOpen, setIsOpen, queryParameters, setQueryParameters, ...props }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Errors
  const [errors, setErrors] = useState([]);

  // Data
  const { data: ciStatusesData = [], error: ciStatusesError } = useItemQuery(
    [`cistatuses`],
    () => readCiStatuses({}),
    true,
  );
  const { data: ciTypesData = [], error: ciTypesError } = useItemQuery([`citypes`], () => readCiTypes({}), true);
  const { data: sitesData = [], error: sitesError } = useItemQuery([`sites`], () => readSites({}), true);
  const { data: usersData = [], error: usersError } = useItemQuery([`users`], () => readUsers({}), true);

  // Effects
  useEffect(() => {
    const newErrors = [];
    if (ciStatusesError) {
      newErrors.push({
        message: ciStatusesError?.message || t("generic.error"),
        type: "error",
        details: ciStatusesError,
      });
    }
    if (ciTypesError) {
      newErrors.push({ message: ciTypesError?.message || t("generic.error"), type: "error", details: ciTypesError });
    }
    if (sitesError) {
      newErrors.push({ message: sitesError?.message || t("generic.error"), type: "error", details: sitesError });
    }
    if (usersError) {
      newErrors.push({ message: usersError?.message || t("generic.error"), type: "error", details: usersError });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [ciStatusesError, ciTypesError, sitesError, usersError, t]);

  // Sync queryParameters from URL when drawer opens
  useEffect(() => {
    if (isOpen) {
      const ciQuerySchema = {
        name: "string",
        type_id: "number",
        status_id: "number",
        owner_id: "number",
        site_id: "number",
      };
      setQueryParameters(parseQueryParams(location.search, ciQuerySchema));
    }
  }, [isOpen, location.search, setQueryParameters]);

  // Handlers
  const handleApplyFilters = () => {
    const searchString = buildQueryString(queryParameters);
    navigate({ search: searchString }, { replace: true });
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    setQueryParameters({ name: null, type_id: null });
    navigate({ search: "" }, { replace: true });
    setIsOpen(false);
  };

  return (
    <Drawer
      position="end"
      open={isOpen}
      onOpenChange={(event, data) => setIsOpen(data.open)}
      size="small"
      modalType="non-modal"
      {...props}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              aria-label="Close"
              onClick={() => setIsOpen(false)}
            />
          }
        >
          {t("generic.filters")}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
          <Field label={t("generic.name")}>
            <Input
              type="text"
              value={queryParameters?.name || ""}
              onChange={(e) => setQueryParameters((prev) => ({ ...prev, name: e.target.value }))}
            />
          </Field>
          <Field label={t("generic.type")}>
            <GenericComboBox
              items={ciTypesData || []}
              value={queryParameters?.type_id}
              valueKey="id"
              displayKey="name"
              itemTemplate={(item) => <CiTypeTemplate ciType={item} />}
              onOptionSelect={(newTypeId) => setQueryParameters((prev) => ({ ...prev, type_id: newTypeId }))}
            />
          </Field>
          <Field label={t("generic.status")}>
            <GenericComboBox
              items={ciStatusesData || []}
              value={queryParameters?.status_id}
              valueKey="id"
              displayKey="name"
              onOptionSelect={(newStatusId) => setQueryParameters((prev) => ({ ...prev, status_id: newStatusId }))}
            />
          </Field>
          <Field label={t("generic.owner")}>
            <GenericComboBox
              items={usersData || []}
              value={queryParameters?.owner_id}
              valueKey="id"
              displayKey="name"
              onOptionSelect={(newUserId) => setQueryParameters((prev) => ({ ...prev, owner_id: newUserId }))}
            />
          </Field>
          <Field label={t("generic.site")}>
            <GenericComboBox
              items={sitesData || []}
              value={queryParameters?.site_id}
              valueKey="id"
              displayKey="name"
              onOptionSelect={(newSiteId) => setQueryParameters((prev) => ({ ...prev, site_id: newSiteId }))}
            />
          </Field>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div style={{ display: "flex", gap: "1rem", width: "100%" }}>
          <Button appearance="primary" onClick={handleApplyFilters} style={{ flex: 1 }}>
            {t("generic.apply")}
          </Button>
          <Button appearance="secondary" onClick={handleClearFilters} style={{ flex: 1 }}>
            {t("generic.reset")}
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default CiFiltersPanel;
